import { algoliasearch } from 'algoliasearch'
import { Collection } from './jsonapi'
import { statusCodes as diamondStatusCodes } from './diamond'
import { ALGOLIA_INDICES, LIST_PAGE_LENGTH } from '../constants'

const indices = ALGOLIA_INDICES[ALGOLIA_ENV]

let _client = null
function getClient() {
  if (!_client) {
    if (!ALGOLIA_APPID || !ALGOLIA_APIKEY) {
      console.warn('Algolia credentials not configured — search will not work.')
      return null
    }
    _client = algoliasearch(ALGOLIA_APPID, ALGOLIA_APIKEY)
  }
  return _client
}

/**
 * Creates an index wrapper compatible with the algoliasearch v3 / autocomplete.js
 * callback signature: index.search(query, params, callback(err, result)).
 * Internally uses the v5 client.searchSingleIndex() Promise API.
 * This keeps AutoComplete.js / autocomplete.sources.hits() working unchanged.
 */
function makeIndex(indexNameFn) {
  return {
    search(query, params, cb) {
      const client = getClient()
      if (!client) {
        if (typeof cb === 'function') cb(null, { hits: [] })
        return Promise.resolve({ hits: [] })
      }
      const { facets, ...rest } = params || {}
      const searchParams = {
        query: query || '',
        ...rest,
        ...(facets !== undefined
          ? { facets: Array.isArray(facets) ? facets : [facets] }
          : {}),
      }
      const promise = client.searchSingleIndex({
        indexName: indexNameFn(),
        searchParams,
      })
      if (typeof cb === 'function') {
        promise.then(result => cb(null, result), err => cb(err, { hits: [] }))
      }
      return promise
    },
  }
}

export const retailersIndex = makeIndex(() => indices.retailers)
export const inventoryIndex = makeIndex(() => indices.inventory)

export function search(indexObj, type, query, page, filters) {
  const params = {
    page,
    hitsPerPage: LIST_PAGE_LENGTH,
    ...(filters ? { facets: ['*'], filters } : {}),
  }
  return new Promise((resolve, reject) => {
    indexObj.search(query || '', params, (err, result) => {
      if (err) return reject(err)
      resolve(
        new Collection({
          data: result.hits.map(hit => ({
            id: `${hit.createdDay}/${hit.objectID}`,
            type,
            source: 'search',
            attributes: {
              ...hit,
              // DataTables column expects 'uuid'; Algolia uses objectID
              uuid: hit.uuid || hit.objectID || hit.internalId || '',
            },
          })),
          meta: {
            pageSize: result.hitsPerPage,
            pageNumber: `${result.page + 1}`,
            recordsTotal: result.nbHits,
            'total-pages': result.nbPages,
            query: result.query,
          },
        })
      )
    })
  })
}

export function searchInventory(query, page, retailerId, setting, jewelryType) {
  let filters = []
  if (retailerId) {
    filters.push(`retailerId:${retailerId}`)
    filters.push(`statusCode != ${diamondStatusCodes.DELETED.value}`)
  }
  if (jewelryType) {
    filters.push(`jewelryType:${jewelryType}`)
  }
  filters = filters.join(' AND ')
  return search(inventoryIndex, 'inventory', query, page, filters)
}

export function searchRetailers(query, page, filters) {
  return search(retailersIndex, 'retailer', query, page, filters)
}
